/* ariro-clickid-cart.js - copies advertising click IDs into Shopify cart attributes
   so GoKwik forwards them onto the order record.
   Added 2026-09-12 by the Gulshan team. Spec: 15-ops-audit/drafts/THEME-CLICKID-CAPTURE-SPEC.md
   v3 2026-09-13: click IDs now EXPIRE, the full key set is always written, and the
   real first-seen click time is emitted. See CHANGE-LOG B86 for why.
   v3 also NAMESPACES our localStorage under 'ar_' - another app on this store
   (GoKwik/BIK) writes the bare 'gclid' key, which made v2 skip its own stamp.
   v4 2026-09-13: also captures the Meta _fbp cookie as ar_fbp. PurchaseNet sent
   fbp on ZERO events (EMQ 8.0) while the browser Purchase event on the same
   dataset carries it on 96.3% (EMQ 9.3). fbp alone justifies a cart write only
   on a real cart/checkout interaction - never on a bare page load.
   SAFE TO REMOVE: delete the one <script src> line in layout/theme.liquid.
   An unreferenced asset does nothing. */
(function () {
  'use strict';

  var MAP = {
    ar_gclid: 'gclid',
    ar_gbraid: 'gbraid',
    ar_wbraid: 'wbraid',
    ar_fbclid: 'fbclid'
  };

  /* 90 days = Google's conversion window (CHANGE-LOG B77). A click older than the
     action's window is rejected outright on upload, so a stored id past this is
     worthless and actively harmful - it would credit paid for an organic order. */
  var MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

  /* set when this pageview expired a stored id, so the cart gets cleaned even
     though the current session has no click id of its own. */
  var expired = false;

  function qp(name) {
    try { return new URLSearchParams(window.location.search).get(name) || ''; }
    catch (e) { return ''; }
  }

  function ls(key) {
    try { return window.localStorage.getItem(key) || ''; } catch (e) { return ''; }
  }

  function lsSet(key, val) {
    try { window.localStorage.setItem(key, val); } catch (e) {}
  }

  function lsDel(key) {
    try { window.localStorage.removeItem(key); } catch (e) {}
  }

  function cookie(name) {
    try {
      var m = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
      return m ? decodeURIComponent(m.pop()) : '';
    } catch (e) { return ''; }
  }

  /* Last touch: a click ID on the CURRENT url beats anything stored.
     Each stored id carries a first-seen stamp and expires at MAX_AGE_MS.

     v3 NAMESPACING - load bearing. Another app on this store writes the BARE
     'gclid' key (alongside utm_source, utm_medium, discount_code...). v2 keyed
     off that same name, so when their write landed first our "has it changed?"
     guard saw no change and skipped writing our own stamp. We now own
     'ar_<key>' and 'ar_<key>_first' and never write the bare key. */
  function resolve(srcKey) {
    var ourKey = 'ar_' + srcKey;
    var stampKey = ourKey + '_first';
    var fresh = qp(srcKey);
    if (fresh) {
      /* only stamp when OUR value changes - the same id arriving again is the
         same click, not a new one, so its age must not reset. */
      if (ls(ourKey) !== fresh) {
        lsSet(ourKey, fresh);
        lsSet(stampKey, String(Date.now()));
      }
      return fresh;
    }
    var stored = ls(ourKey);
    if (!stored) {
      /* one-time migration off v1's bare key. Guarded by a flag so an expired
         id can never be resurrected from whatever the other app still holds -
         that would reset the clock forever and defeat expiry entirely. */
      if (ls('ar_migrated') !== '1') {
        lsSet('ar_migrated', '1');
        var legacy = ls(srcKey);
        if (legacy) {
          lsSet(ourKey, legacy);
          lsSet(stampKey, String(Date.now()));
          return legacy;
        }
      }
      return '';
    }
    var first = parseInt(ls(stampKey), 10);
    if (!first) {
      lsSet(stampKey, String(Date.now()));
      return stored;
    }
    if ((Date.now() - first) > MAX_AGE_MS) {
      lsDel(ourKey);
      lsDel(stampKey);
      expired = true;
      return '';
    }
    return stored;
  }

  function collect(force) {
    var attrs = {}, any = false, k, v, f, firstMax = 0;
    for (k in MAP) {
      if (!Object.prototype.hasOwnProperty.call(MAP, k)) continue;
      v = resolve(MAP[k]);
      /* v2: ALWAYS emit the key. '' removes it, so a stale value left on the
         cart from an earlier session can never survive into an order. */
      attrs[k] = v ? String(v).slice(0, 255) : '';
      if (v) {
        any = true;
        f = parseInt(ls('ar_' + MAP[k] + '_first'), 10);
        if (f && f > firstMax) { firstMax = f; }
      }
    }
    var fbc = cookie('_fbc');
    attrs.ar_fbc = fbc ? String(fbc).slice(0, 255) : '';
    if (fbc) { any = true; }
    /* v4: _fbp is the Meta pixel's own browser cookie - set for EVERY visitor, not
       only ad clickers. Read fresh each pageview exactly like ar_fbc, never stored,
       because our copy would outlive the pixel's cookie. It deliberately does NOT
       set `any`: ar_click_ts keys off that, and stamping a click time on an organic
       order would break the B80 freshness ordering. Instead fbp alone justifies a
       write only when `force` is set - a real cart or checkout interaction - because
       Shopify accepts attributes on an EMPTY cart, so writing on bare page load
       would mint an attributed cart for every browsing visitor. */
    var fbp = cookie('_fbp');
    attrs.ar_fbp = fbp ? String(fbp).slice(0, 255) : '';
    /* ar_click_ts stays the CAPTURE time for continuity with v1 orders.
       ar_click_first_ts is the REAL click time - use this one for age. */
    attrs.ar_click_ts = any ? String(Date.now()) : '';
    attrs.ar_click_first_ts = firstMax ? String(firstMax) : '';
    return (any || expired || (force && fbp)) ? attrs : null;
  }

  var lastSent = '';

  function push(force) {
    var attrs = collect(force);
    if (!attrs) return;
    var stable = {}, keys = Object.keys(attrs).sort(), i;
    for (i = 0; i < keys.length; i++) {
      if (keys[i] !== 'ar_click_ts') { stable[keys[i]] = attrs[keys[i]]; }
    }
    var sig = JSON.stringify(stable);
    if (sig === lastSent) return;
    lastSent = sig;
    try {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attrs }),
        keepalive: true
      }).catch(function () {});
    } catch (e) {}
  }

  /* 1. On load. Shopify accepts attributes on an empty cart - which is exactly why
        v4 passes force=false here: fbp alone must not write on a bare page load.
        Note the wrappers: addEventListener hands the handler an Event object, which
        is truthy, so passing `push` directly would silently force every load. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { push(false); });
  } else {
    push(false);
  }

  /* 2. After a cart change - some operations replace the cart object. */
  ['cart:updated', 'cart:refresh', 'ajaxCart.afterCartLoad', 'product:added']
    .forEach(function (evt) {
      document.addEventListener(evt, function () { push(true); });
    });

  /* 3. Immediately before checkout opens - the cart may pre-date this script
        (returning visitor, recovered cart). Last moment before GoKwik takes over. */
  document.addEventListener('click', function (ev) {
    try {
      var t = ev.target && ev.target.closest &&
        ev.target.closest('[name="checkout"], .gokwik-checkout, [data-gokwik], a[href*="/checkout"]');
      if (t) { lastSent = ''; push(true); }
    } catch (e) {}
  }, true);

  /* NOTE: the spec's optional window.fetch monkey-patch (its trigger 3) is
     DELIBERATELY OMITTED in v1 - spec risk #3. Triggers 1-3 above cover the
     cases that matter without wrapping a global another app may also wrap. */
})();
